import React, { useCallback, useMemo, useState } from 'react'
import T from 'prop-types'
import { Trans, useTranslation } from 'react-i18next'
import { Box, CircularProgress, Link, Typography } from '@mui/material'
import { addYears, format } from 'date-fns'
import { INVENTORY_STATE_RETURNED } from 'lib/inventoryStates'
import * as Yup from 'yup'
import { Form, Formik } from 'formik'
import axios from 'axios'
import config from 'config'

import ConfirmationDialog from 'components/atoms/ConfirmationDialog'
import FormDatePicker from 'components/atoms/FormDatePicker'

import useReport from 'hooks/useReport'

import {
  INVENTORY_ACTION_DOWNLOAD_DOCUMENTS,
  INVENTORY_ACTION_DOWNLOAD_REPORT,
  INVENTORY_ACTION_PRICE_CHANGES,
  INVENTORY_ACTION_PURCHASE_REQUEST,
  INVENTORY_ACTION_STATE_HISTORY_INFO,
} from './stateChange/const'

const COMMON_REQUIRED_FIELD = 'common.requiredField'

const InventoryActionsDialog = ({
  selected = [],
  action,
  selectedCarForDocuments,
  documents = [],
  isFetching = false,
  setSelectedCarForDocuments = () => {},
  handleCloseConfirmationDialogOriginal = () => {},
  handleConfirmConfirmationDialog = () => {},
  getStateChangeConfirmationFormFields = () => {},
  handlePrepareFilters = () => {},
  setFormLoading = () => {},

  formLoading = false,
  confirmLoading = false,
  openConfirmationDialog = false,
  readOnly = false,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { t } = useTranslation()

  const { saveReport } = useReport()

  const [initialDownloadReportValues /*setInitialDownloadReportValues*/] =
    useState({
      startDate: null,
      endDate: null,
    })

  const DownloadReportFormSchema = useMemo(() => {
    const date = Yup.date().typeError(t('common.date.error.invalidFormat'))
    return Yup.object().shape({
      startDate: date.required(t(COMMON_REQUIRED_FIELD)),
      endDate: date.required(t(COMMON_REQUIRED_FIELD)),
    })
  }, [t])

  const numberFormatter = useMemo(() => new Intl.NumberFormat('it-IT'), [])

  const handleDownloadReportSubmit = async () => {
    setFormLoading(true)
    await saveReport('report_inventory', JSON.stringify(handlePrepareFilters()))
    setFormLoading(false)
  }

  const handleCloseConfirmationDialog = useCallback(() => {
    setSelectedCarForDocuments()
    handleCloseConfirmationDialogOriginal()
  }, [handleCloseConfirmationDialogOriginal, setSelectedCarForDocuments])

  /**
   * @type {React.RefObject<import('formik').FormikProps<{}>>}
   */
  const formikRef = React.useRef(null)

  const confirmationFormFields = useMemo(() => {
    return getStateChangeConfirmationFormFields(action, readOnly, selected?.[0])
  }, [action, readOnly, selected, getStateChangeConfirmationFormFields])

  return (
    <>
      <ConfirmationDialog
        title={t(
          `partnerInventory.action.${action}${
            action === INVENTORY_ACTION_PURCHASE_REQUEST &&
            selected.every((car) => car.state === INVENTORY_STATE_RETURNED)
              ? '-b2b'
              : ''
          }.title`
        )}
        contentComponent="div"
        content={
          <>
            {action === INVENTORY_ACTION_DOWNLOAD_DOCUMENTS && isFetching ? (
              <Box
                display="flex"
                justifyContent={'center'}
                alignItems={'center'}
              >
                <CircularProgress />
              </Box>
            ) : null}
            {!(
              action === INVENTORY_ACTION_DOWNLOAD_DOCUMENTS && isFetching
            ) && (
              <Typography variant="body2">
                <Trans
                  i18nKey={`partnerInventory.action.${action}${
                    action === INVENTORY_ACTION_PURCHASE_REQUEST &&
                    selected.every(
                      (car) => car.state === INVENTORY_STATE_RETURNED
                    )
                      ? '-b2b'
                      : ''
                  }.content${
                    action === INVENTORY_ACTION_DOWNLOAD_DOCUMENTS &&
                    !documents?.length
                      ? 'Empty'
                      : ''
                  }`}
                  components={{ bold: <strong /> }}
                />
              </Typography>
            )}

            {confirmationFormFields}

            {action === INVENTORY_ACTION_DOWNLOAD_DOCUMENTS && (
              <>
                <Box pb={2} />
                {documents?.map((document, i) => (
                  <React.Fragment key={`${document.file}_file_${i}`}>
                    <Link
                      component={Box}
                      variant="body2"
                      onClick={async () => {
                        const result = await axios.get(
                          `${config.apiBaseUrl}inventory/sign-download-document?filename=${document.file}`
                        )
                        window.location = result.data?.body?.signedUrl
                      }}
                      color="primary"
                      sx={{ /* textDecoration: 'none', */ cursor: 'pointer' }}
                    >
                      {document.name}
                    </Link>
                    <Box pb={1} />
                  </React.Fragment>
                ))}
              </>
            )}
            {action === INVENTORY_ACTION_STATE_HISTORY_INFO && (
              <>
                <Box pb={2} />
                <table style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th align="left" sx={{ weight: 'normal' }}>
                        <Typography component="span" color="textSecondary">
                          {t(
                            `partnerInventory.action.${INVENTORY_ACTION_STATE_HISTORY_INFO}.table.state`
                          )}
                        </Typography>
                      </th>
                      <th align="left">
                        <Typography component="span" color="textSecondary">
                          {t(
                            `partnerInventory.action.${INVENTORY_ACTION_STATE_HISTORY_INFO}.table.stateChangeAt`
                          )}
                        </Typography>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCarForDocuments?.stateHistoryList
                      ?.sort((historyA, historyB) => {
                        if (
                          historyA.state_change_at < historyB.state_change_at
                        ) {
                          return -1
                        }
                        return 1
                      })
                      .map((history, i) => (
                        <React.Fragment
                          key={`${selectedCarForDocuments.inventoryId}_info_${i}`}
                        >
                          <tr>
                            <td>
                              {t(`partnerInventory.status.${history.state}`)}
                            </td>
                            <td>
                              {history.state_change_at &&
                                format(
                                  new Date(history.state_change_at),
                                  t('common.date.formatDMY')
                                )}
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                  </tbody>
                </table>
              </>
            )}
            {action === INVENTORY_ACTION_PRICE_CHANGES && (
              <>
                <Box pb={2} />
                <table style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th align="left" sx={{ weight: 'normal' }}>
                        <Typography component="span" color="textSecondary">
                          {t(
                            `partnerInventory.action.${INVENTORY_ACTION_PRICE_CHANGES}.table.suggestedPrice`
                          )}
                        </Typography>
                      </th>
                      <th align="left">
                        <Typography component="span" color="textSecondary">
                          {t(
                            `partnerInventory.action.${INVENTORY_ACTION_PRICE_CHANGES}.table.stateChangeAt`
                          )}
                        </Typography>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCarForDocuments?.priceList?.map((price, i) => (
                      <React.Fragment
                        key={`${selectedCarForDocuments.inventoryId}_price_${i}`}
                      >
                        <tr>
                          <td>
                            {numberFormatter.format(price.price + price.fee)}
                          </td>
                          <td>
                            {format(
                              new Date(price.timestamp),
                              t('common.date.formatDMY')
                            )}
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            {action === INVENTORY_ACTION_DOWNLOAD_REPORT && (
              <Formik
                innerRef={formikRef}
                validationSchema={DownloadReportFormSchema}
                enableReinitialize={true}
                onSubmit={handleDownloadReportSubmit}
                initialValues={initialDownloadReportValues}
              >
                {({ values }) => (
                  <Form>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 2,
                      }}
                    >
                      <FormDatePicker
                        name="startDate"
                        label={t(`partnerInventory.action.${action}.startDate`)}
                        minDate={addYears(values.endDate, -1)}
                        data-testid="start-date"
                      />
                      <FormDatePicker
                        name="endDate"
                        label={t(`partnerInventory.action.${action}.endDate`)}
                        maxDate={addYears(values.startDate, 1)}
                        data-testid="end-date"
                      />
                    </Box>
                  </Form>
                )}
              </Formik>
            )}
          </>
        }
        readOnly={confirmLoading || formLoading}
        open={openConfirmationDialog}
        handleClose={handleCloseConfirmationDialog}
        handleSubmit={
          [
            INVENTORY_ACTION_DOWNLOAD_DOCUMENTS,
            INVENTORY_ACTION_PRICE_CHANGES,
            INVENTORY_ACTION_STATE_HISTORY_INFO,
          ].includes(action)
            ? handleCloseConfirmationDialog
            : handleConfirmConfirmationDialog
        }
        hideCancel={[
          INVENTORY_ACTION_DOWNLOAD_DOCUMENTS,
          INVENTORY_ACTION_PRICE_CHANGES,
          INVENTORY_ACTION_STATE_HISTORY_INFO,
        ].includes(action)}
        confirmLabel={
          [
            INVENTORY_ACTION_DOWNLOAD_DOCUMENTS,
            INVENTORY_ACTION_PRICE_CHANGES,
            INVENTORY_ACTION_STATE_HISTORY_INFO,
          ].includes(action)
            ? t(`common.form.close`)
            : undefined
        }
      />
    </>
  )
}

InventoryActionsDialog.propTypes = {
  selected: T.arrayOf(T.any),
  action: T.string,
  handleCloseConfirmationDialogOriginal: T.func,
  handleConfirmConfirmationDialog: T.func,
  getStateChangeConfirmationFormFields: T.func,
  handlePrepareFilters: T.func,
  confirmLoading: T.bool,
  openConfirmationDialog: T.bool,
  readOnly: T.bool,
}

export default InventoryActionsDialog
